import { useMemo, useState } from 'react';
import type { Transition } from '@/api/types';
import { formatCompact } from './TrendChart';

interface FlowDiagramProps {
  transitions: Transition[];
  /** Columns to draw. Beyond this the ribbons are too thin to read. */
  maxDepth?: number;
  /** Nodes kept per column, by volume. The rest fold into "other". */
  maxPerColumn?: number;
}

interface FlowNode {
  page: string;
  depth: number;
  value: number;
  y: number;
  height: number;
}

/**
 * Where visitors flow, page to page.
 *
 * <b>Why a flow and not the table it replaces.</b> The question is "where does traffic go from
 * here", which is a question about proportion and branching — a table of from/to/count makes the
 * reader reconstruct the shape in their head. A ribbon whose thickness IS the session count shows
 * it directly, and the drop-off between columns becomes visible rather than arithmetic.
 *
 * <b>One hue, varying only in opacity.</b> The ribbons encode a single measure (sessions), so this
 * is a magnitude encoding, not an identity one — giving each path its own colour would imply a
 * category that does not exist and would burn the two validated categorical slots for nothing.
 *
 * The table view is kept alongside, not replaced: a diagram cannot be read by a screen reader, and
 * exact numbers are easier to compare in a column.
 */
export function FlowDiagram({ transitions, maxDepth = 4, maxPerColumn = 6 }: FlowDiagramProps) {
  const [hover, setHover] = useState<string | null>(null);

  const model = useMemo(() => buildModel(transitions, maxDepth, maxPerColumn), [transitions, maxDepth, maxPerColumn]);

  if (model.columns.length < 2) {
    return (
      <div className="empty-state t-body">
        Not enough multi-page visits yet to draw a flow. Two pages in one session is the minimum.
      </div>
    );
  }

  const { columns, links, width, height } = model;

  return (
    <div className="flow">
      <div className="flow-scroll">
        <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img"
             aria-label="Page-to-page flow, ribbon thickness is session count">
          {links.map((link) => {
            const active = hover === null || hover === link.from.page || hover === link.to.page;
            return (
              <path
                key={`${link.from.page}@${link.from.depth}->${link.to.page}`}
                d={ribbon(link)}
                className="flow-ribbon"
                // Opacity is the only channel that changes — thickness already carries volume, so
                // dimming is a focus cue rather than a second encoding of the same number.
                style={{ opacity: active ? 0.55 : 0.12 }}
              >
                <title>{`${link.from.page} → ${link.to.page}: ${link.value} sessions`}</title>
              </path>
            );
          })}

          {columns.flatMap((column) => column.map((node) => (
            <g key={`${node.page}@${node.depth}`}
               onMouseEnter={() => setHover(node.page)}
               onMouseLeave={() => setHover(null)}>
              <rect
                x={node.depth * COLUMN_STRIDE}
                y={node.y}
                width={NODE_WIDTH}
                height={node.height}
                rx={2}
                className={`flow-node${hover === node.page ? ' is-hover' : ''}`}
              />
              <text
                x={node.depth * COLUMN_STRIDE + NODE_WIDTH + 6}
                y={node.y + node.height / 2}
                dominantBaseline="middle"
                className="flow-label"
              >
                {node.page}
              </text>
              <title>{`${node.page}: ${node.value} sessions`}</title>
            </g>
          )))}
        </svg>
      </div>

      <p className="t-caption subtle" style={{ margin: '8px 0 0' }}>
        Ribbon thickness is sessions. Columns are hops from the start of the visit — column 1 is the
        entry page. Only the top {maxPerColumn} pages per column are drawn.
      </p>
    </div>
  );
}

const NODE_WIDTH = 10;
const COLUMN_STRIDE = 190;
const GAP = 6;
const MIN_NODE = 3;

/** Room for the final column's labels, which sit outside the last stride. */
const LABEL_GUTTER = 150;

function buildModel(transitions: Transition[], maxDepth: number, maxPerColumn: number) {
  const byDepth = new Map<number, Map<string, number>>();

  // A page's column is its HOP NUMBER, not its identity: the same page reached second and fourth is
  // two different moments in a visit, and merging them would draw a cycle that the data never shows.
  for (const t of transitions) {
    if (t.position > maxDepth) continue;
    add(byDepth, t.position - 1, t.from, t.sessions);
    add(byDepth, t.position, t.to, t.sessions);
  }

  const columns: FlowNode[][] = [];
  const depths = [...byDepth.keys()].sort((a, b) => a - b).slice(0, maxDepth + 1);

  let tallest = 0;
  for (const depth of depths) {
    const entries = [...byDepth.get(depth)!.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, maxPerColumn);
    tallest = Math.max(tallest, entries.reduce((sum, e) => sum + e[1], 0));
  }
  if (tallest === 0) return { columns: [], links: [], width: 0, height: 0 };

  const height = Math.max(160, Math.min(420, tallest * 8));
  const scale = (height - GAP * (maxPerColumn - 1)) / tallest;

  for (const depth of depths) {
    const entries = [...byDepth.get(depth)!.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, maxPerColumn);

    let y = 0;
    const column: FlowNode[] = [];
    for (const [page, value] of entries) {
      const h = Math.max(MIN_NODE, value * scale);
      column.push({ page, depth, value, y, height: h });
      y += h + GAP;
    }
    columns.push(column);
  }

  // Ribbons connect a node to its next-column counterpart, stacked in node order so they do not
  // all converge on one point.
  const links: { from: FlowNode; to: FlowNode; value: number; y0: number; y1: number; h: number }[] = [];
  const offsets = new Map<string, number>();

  for (const t of transitions) {
    const from = columns[t.position - 1]?.find((n) => n.page === t.from);
    const to = columns[t.position]?.find((n) => n.page === t.to);
    if (!from || !to) continue;

    const fk = `${from.page}@${from.depth}`;
    const tk = `${to.page}@${to.depth}in`;
    const h = Math.max(1, t.sessions * scale);
    const y0 = from.y + (offsets.get(fk) ?? 0);
    const y1 = to.y + (offsets.get(tk) ?? 0);
    offsets.set(fk, (offsets.get(fk) ?? 0) + h);
    offsets.set(tk, (offsets.get(tk) ?? 0) + h);

    links.push({ from, to, value: t.sessions, y0, y1, h });
  }

  // The last column's labels sit to the RIGHT of its nodes, outside the stride that positions
  // them — so the viewBox needs room for that text or the final page names are clipped off the
  // edge, which is exactly where the most interesting pages (the destinations) end up.
  const width = (depths.length - 1) * COLUMN_STRIDE + NODE_WIDTH + LABEL_GUTTER;
  return { columns, links, width, height };
}

function add(map: Map<number, Map<string, number>>, depth: number, page: string, value: number) {
  if (depth < 0) return;
  if (!map.has(depth)) map.set(depth, new Map());
  const column = map.get(depth)!;
  column.set(page, (column.get(page) ?? 0) + value);
}

/** A cubic ribbon — curved rather than straight so crossing flows stay distinguishable. */
function ribbon(link: { from: FlowNode; to: FlowNode; y0: number; y1: number; h: number }): string {
  const x0 = link.from.depth * COLUMN_STRIDE + NODE_WIDTH;
  const x1 = link.to.depth * COLUMN_STRIDE;
  const mid = (x0 + x1) / 2;
  const { y0, y1, h } = link;

  return [
    `M ${x0} ${y0}`,
    `C ${mid} ${y0}, ${mid} ${y1}, ${x1} ${y1}`,
    `L ${x1} ${y1 + h}`,
    `C ${mid} ${y1 + h}, ${mid} ${y0 + h}, ${x0} ${y0 + h}`,
    'Z',
  ].join(' ');
}

/** Compact "N sessions" for tooltips and legends. */
export function flowLabel(value: number): string {
  return `${formatCompact(value)} session${value === 1 ? '' : 's'}`;
}
